import React, { createContext, useContext, useState, useCallback } from 'react';
import { isFeatureEnabled } from '../../utils/featureFlagsStore';

export interface WalkthroughStep {
  element: string;
  title: string;
  description: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export interface WalkthroughConfig {
  id: string;
  title?: string;
  steps: WalkthroughStep[];
}

interface WalkthroughContextValue {
  isActive: boolean;
  currentStepIndex: number;
  currentStep: WalkthroughStep | null;
  totalSteps: number;
  walkthroughTitle: string;
  startWalkthrough: (id: string) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipWalkthrough: () => void;
}

const WalkthroughContext = createContext<WalkthroughContextValue>({
  isActive: false,
  currentStepIndex: 0,
  currentStep: null,
  totalSteps: 0,
  walkthroughTitle: '',
  startWalkthrough: () => {},
  nextStep: () => {},
  prevStep: () => {},
  skipWalkthrough: () => {},
});

export const useWalkthrough = () => useContext(WalkthroughContext);

// Static import of all walkthrough configs
const walkthroughConfigs: Record<string, WalkthroughConfig> = {};

// Load walkthrough configs
function loadConfigs() {
  const configs: [string, WalkthroughConfig][] = [
    ['create-order', require('../../help/walkthroughs/create-order.json')],
    ['create-batch', require('../../help/walkthroughs/create-batch.json')],
    ['create-bill', require('../../help/walkthroughs/create-bill.json')],
    ['add-material', require('../../help/walkthroughs/add-material.json')],
    ['add-worker', require('../../help/walkthroughs/add-worker.json')],
  ];
  configs.forEach(([id, config]) => { walkthroughConfigs[id] = config; });
}
loadConfigs();

const walkthroughRequiredFeatures: Record<string, string[]> = {
  'create-order': ['ORDERS'],
  'create-batch': ['PRODUCTION', 'PROCESSING'],
  'create-bill': ['FINANCE'],
  'add-material': ['INVENTORY'],
  'add-worker': ['WORKFORCE'],
};

function walkthroughFeaturesSatisfied(id: string): boolean {
  const required = walkthroughRequiredFeatures[id];
  if (!required?.length) return true;
  if (id === 'create-batch') {
    return required.some((code) => isFeatureEnabled(code));
  }
  return required.every((code) => isFeatureEnabled(code));
}

export const WalkthroughProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<WalkthroughConfig | null>(null);
  const [stepIndex, setStepIndex] = useState(0);

  const startWalkthrough = useCallback((id: string) => {
    if (!walkthroughFeaturesSatisfied(id)) {
      return;
    }
    const cfg = walkthroughConfigs[id];
    if (cfg && cfg.steps.length > 0) {
      setConfig(cfg);
      setStepIndex(0);
    }
  }, []);

  const skipWalkthrough = useCallback(() => {
    setConfig(null);
    setStepIndex(0);
  }, []);

  const nextStep = useCallback(() => {
    if (!config) return;
    if (stepIndex < config.steps.length - 1) {
      setStepIndex(i => i + 1);
    } else {
      skipWalkthrough();
    }
  }, [config, stepIndex, skipWalkthrough]);

  const prevStep = useCallback(() => {
    setStepIndex(i => Math.max(0, i - 1));
  }, []);

  const value: WalkthroughContextValue = {
    isActive: !!config,
    currentStepIndex: stepIndex,
    currentStep: config?.steps[stepIndex] || null,
    totalSteps: config?.steps.length || 0,
    walkthroughTitle: config?.title || '',
    startWalkthrough,
    nextStep,
    prevStep,
    skipWalkthrough,
  };

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
    </WalkthroughContext.Provider>
  );
};
